import { t } from '@lingui/macro';
import { useCallback } from 'react';

import { fetchJson } from '@api/ApiHelper';
import { ToastLooks, useCreateToast } from '@components/common/toast';

interface UpdateFaceNameParams {
  personId: string;
  name: string;
  old_name: string;
}

interface UpdateFaceNameResponse {
  id: string;
  identity_name: string;
  source_url: string;
}

export const useUpdateFaceName = (
  onSuccess?: () => void
): { updateFaceName: (params: UpdateFaceNameParams) => Promise<boolean> } => {
  const { toast } = useCreateToast();

  const updateFaceName = useCallback(
    async ({ personId, name, old_name }: UpdateFaceNameParams): Promise<boolean> => {
      try {
        // Get brandfolder key from global BFG object
        const brandfolderKey = BFG.brandfolder_key;

        if (!brandfolderKey) {
          toast(t`Brandfolder not found`, ToastLooks.Error);
          return false;
        }

        if (!personId || !name.trim()) {
          toast(t`Person ID and name are required`, ToastLooks.Error);
          return false;
        }

        // Make API call to update face name
        await fetchJson<UpdateFaceNameResponse>({
          url: `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/identities/${personId}`,
          method: 'PUT',
          body: {
            name: name.trim(),
            old_name: old_name.trim(),
          },
        });

        // Call success callback if provided (e.g., to refetch data)
        onSuccess?.();

        return true;
      } catch (error) {
        toast(t`Failed to update person name. Please try again.`, ToastLooks.Error);
        return false;
      }
    },
    [onSuccess, toast]
  );

  return { updateFaceName };
};
